import React from 'react';
import { Bell, BellOff, Volume2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';

const fmtTime = (ts) => new Date(ts).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

function ToneRow({ id, emoji, title, hint, checked, onChange, onTest, disabled }) {
    return (
        <div className="flex items-center gap-2 py-1.5" data-testid={`sound-row-${id}`}>
            <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={`sound-${id}-switch`} className="data-[state=checked]:bg-orange-500" />
            <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-slate-100">{emoji} {title}</div>
                <div className="text-[10px] text-slate-400">{hint}</div>
            </div>
            <button type="button" onClick={onTest} data-testid={`sound-test-${id}`}
                    className="text-[10px] font-bold px-2 py-1 rounded-md border border-slate-600 text-slate-200 hover:bg-slate-700 inline-flex items-center gap-1">
                <Volume2 className="h-3 w-3" /> Dinle
            </button>
        </div>
    );
}

/** Başlıktaki zil: sesli uyarı ayarları (aç/kapat, ses seviyesi, hangi karar çalsın, dinle). */
export default function SoundSettings({ sound }) {
    const { settings, update, test, audio, lastEvent } = sound;
    const on = settings.enabled;
    const locked = on && audio !== 'running' && audio !== 'unsupported';
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid="sound-settings-trigger"
                    aria-label="Sesli uyarı ayarları"
                    title={on ? 'Sesli uyarı açık — AÇ kararı çıkınca ton çalar' : 'Sesli uyarı kapalı'}
                    className={`relative h-8 w-8 flex items-center justify-center rounded-sm border shrink-0 transition-colors ${on ? 'border-orange-500/40 bg-orange-500/10 text-orange-200 hover:bg-orange-500/20' : 'border-slate-700 bg-slate-800 text-slate-500 hover:bg-slate-700'}`}
                >
                    {on ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
                    {locked && <span data-testid="sound-locked-dot" className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-amber-400" />}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 bg-slate-800 border-slate-700 text-slate-100 p-3 space-y-2" data-testid="sound-settings-popover">
                <div className="flex items-center justify-between gap-2">
                    <div>
                        <div className="text-sm font-bold">🔔 Sesli uyarı</div>
                        <div className="text-[10px] text-slate-400">Ekrana bakmadan haberin olsun: yeni karar çıkınca kısa ton.</div>
                    </div>
                    <Switch checked={on} onCheckedChange={(v) => update({ enabled: v })} data-testid="sound-enabled-switch" className="data-[state=checked]:bg-orange-500" />
                </div>
                {locked && (
                    <div data-testid="sound-unlock-hint" className="text-[10px] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-md px-2 py-1.5">
                        Tarayıcı sesi ilk dokunuştan sonra açar — sayfaya bir kez tıkla veya aşağıda "Dinle"ye bas.
                    </div>
                )}
                {audio === 'unsupported' && (
                    <div className="text-[10px] text-rose-200 bg-rose-500/10 border border-rose-500/30 rounded-md px-2 py-1.5">Bu tarayıcı ses üretimini desteklemiyor.</div>
                )}
                <div className="flex items-center gap-2 pt-1">
                    <Volume2 className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <Slider value={[Math.round(settings.volume * 100)]} min={10} max={100} step={5} disabled={!on}
                            onValueChange={([v]) => update({ volume: v / 100 })} data-testid="sound-volume" className="flex-1" />
                    <span className="text-[10px] font-mono text-slate-300 w-8 text-right">%{Math.round(settings.volume * 100)}</span>
                </div>
                <div className="border-t border-slate-700 pt-1">
                    <ToneRow id="ac" emoji="🎯" title="AÇ — resmi sinyal" hint="Belirgin, iki yükselen nota. Taranan tüm coinler."
                             checked={settings.ac} onChange={(v) => update({ ac: v })} onTest={() => test('AC')} disabled={!on} />
                    <ToneRow id="emir" emoji="🔭" title="EMİR KOY — koşullu emir" hint="Yumuşak tek nota. Varsayılan kapalı."
                             checked={settings.emirKoy} onChange={(v) => update({ emirKoy: v })} onTest={() => test('EMIR_KOY')} disabled={!on} />
                </div>
                <div className="text-[10px] text-slate-400 border-t border-slate-700 pt-2" data-testid="sound-last-event">
                    {lastEvent
                        ? <>Son ses: <b className="text-slate-200">{lastEvent.emoji} {lastEvent.action_tr} · {lastEvent.symbol.replace('USDT', '')}</b> · {fmtTime(lastEvent.at)}{!lastEvent.played && ' (ses kilitliydi)'}</>
                        : 'Henüz ses çalmadı. Aynı coin için aynı karar 10 dk içinde tekrar çalmaz; sekme arka plandayken de çalar.'}
                </div>
            </PopoverContent>
        </Popover>
    );
}
